/**
 * Geocentric apparent positions — the foundation
 *
 * Thin layer over astronomy-engine. Returns TROPICAL true-ecliptic-of-date
 * longitude, latitude, distance and true daily motion for the seven grahas.
 *
 * Contract:
 *   - longitude is apparent, geocentric, in the true ecliptic and equinox of date,
 *     which is the frame the ayanāṁśa is defined against;
 *   - speed is the real derivative, sampled either side of the instant, so
 *     retrogression is observed rather than assumed from a table;
 *   - nothing here knows what a rāśi is. Sidereal conversion happens only in
 *     ayanamsa.js, and only once.
 *
 * Do not replace this with mean orbital elements. See common/docs/ASTRONOMY.md.
 *
 * © Manish Jagdish Thatte
 */

import {
  Body, Ecliptic, EquatorFromVector, GeoVector, HelioVector,
  MakeTime, RotateVector, Rotation_EQJ_EQD
} from 'astronomy-engine';
import { J2000 } from './julian.js';

/**
 * The seven physical grahas, in the classical vāra order (Sun … Saturn), which
 * is also the order the weekday and the horā follow. Rāhu and Ketu are not here:
 * they are points, not bodies, and live in nodes.js.
 */
export const GRAHAS = ['sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn'];

const BODY_OF = {
  sun: Body.Sun, moon: Body.Moon, mars: Body.Mars, mercury: Body.Mercury,
  jupiter: Body.Jupiter, venus: Body.Venus, saturn: Body.Saturn
};

/**
 * Build an astronomy-engine time whose **TT** is exactly the JD we were handed.
 *
 * astronomy-engine derives TT from UT with its own ΔT model; we set `tt`
 * directly so that a fixture quoted in TT — every Meeus worked example is — is
 * evaluated at the instant it actually names. `ut` is left as the library
 * computed it, and nothing in this module reads it: earth-rotation quantities
 * live in sidereal-time.js and take JD_UT explicitly.
 */
export function timeFromTT(jdTT) {
  if (!Number.isFinite(jdTT)) throw new TypeError('timeFromTT: jdTT must be finite');
  const t = MakeTime(jdTT - J2000);
  t.tt = jdTT - J2000;
  return t;
}

function bodyOf(graha) {
  const b = BODY_OF[String(graha).toLowerCase()];
  if (!b) throw new RangeError(`unknown graha "${graha}" — expected one of ${GRAHAS.join(', ')}`);
  return b;
}

const norm360 = (d) => ((d % 360) + 360) % 360;

/**
 * Apparent geocentric position of one graha at JD_TT.
 *
 * Returns `{ lon, lat, dist, speed, ra, dec }` — longitude and latitude in
 * degrees in the **true ecliptic and equinox of date**, distance in AU, speed in
 * degrees of longitude per day, right ascension in degrees (not hours) and
 * declination in degrees, both in the **true equator of date**.
 *
 * ⚠ `ra` and `dec` are exposed from the start because ayana bala (Phase 6)
 * needs declination. Adding them later would mean re-opening the one layer that
 * everything else already depends on.
 */
export function grahaPosition(graha, jdTT) {
  const body = bodyOf(graha);
  const t = timeFromTT(jdTT);

  // Apparent: astronomy-engine applies light-travel time and, with the third
  // argument, annual aberration. Ecliptic() converts the J2000 equatorial vector
  // to the TRUE ECLIPTIC OF DATE (ECT) — verified against Meeus 25.b and 47.a;
  // it is not a J2000 ecliptic despite the name.
  const gv = GeoVector(body, t, true);
  const ecl = Ecliptic(gv);
  const equ = EquatorFromVector(RotateVector(Rotation_EQJ_EQD(t), gv));

  return {
    graha: String(graha).toLowerCase(),
    lon: norm360(ecl.elon),
    lat: ecl.elat,
    dist: ecl.vec.Length(),
    speed: dailyMotion(graha, jdTT),
    ra: norm360(equ.ra * 15),
    dec: equ.dec
  };
}

/** Longitude alone, without the derivative — the hot path inside dailyMotion. */
function longitudeAt(body, jdTT) {
  const t = timeFromTT(jdTT);
  return norm360(Ecliptic(GeoVector(body, t, true)).elon);
}

/** Every graha at one instant, keyed by name. */
export function allGrahas(jdTT) {
  const out = {};
  for (const g of GRAHAS) out[g] = grahaPosition(g, jdTT);
  return out;
}

/**
 * True motion in ecliptic longitude, degrees per day, by central difference
 * over ±0.5 d.
 *
 * ⚠ The difference is unwrapped across 0°/360°. Without that, a graha sitting on
 * the cusp of Mīna/Meṣa reports −360°/day and every retrogression test in the
 * project inverts.
 */
export function dailyMotion(graha, jdTT, h = 0.5) {
  const body = bodyOf(graha);
  const before = longitudeAt(body, jdTT - h);
  const after = longitudeAt(body, jdTT + h);
  let d = after - before;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d / (2 * h);
}

/**
 * Is this graha retrograde at this instant?
 *
 * ◆ Measured, never tabulated. The Sun and the Moon never retrograde and this
 * returns false for them by observation, not by a special case.
 */
export function isRetrograde(graha, jdTT) {
  return dailyMotion(graha, jdTT) < 0;
}

/**
 * Combustion arcs in degrees (Parāśara, via Parāśara / BPHS ch. 3).
 *
 * ⚠ A single number per graha is wrong. Mercury and Venus have **different arcs
 * when retrograde**, and a retrograde Mercury at 13° from the Sun is combust on
 * the direct rule and not combust on the correct one.
 */
const COMBUSTION = {
  moon: { direct: 12, retrograde: 12 },
  mars: { direct: 17, retrograde: 17 },
  mercury: { direct: 14, retrograde: 12 },
  jupiter: { direct: 11, retrograde: 11 },
  venus: { direct: 10, retrograde: 8 },
  saturn: { direct: 15, retrograde: 15 }
};

/** The combustion arc for a graha in a given direction of motion. */
export function combustionArc(graha, retrograde = false) {
  const g = String(graha).toLowerCase();
  if (g === 'sun') throw new RangeError('the Sun cannot be combust by itself');
  const arc = COMBUSTION[g];
  if (!arc) throw new RangeError(`no combustion arc defined for "${graha}"`);
  return retrograde ? arc.retrograde : arc.direct;
}

/**
 * Is `graha` combust — within its arc of the Sun in longitude?
 *
 * Both arguments are `grahaPosition` results, so the retrograde state comes
 * from the measured speed rather than from the caller's assumption.
 */
export function isCombust(graha, sun) {
  if (graha.graha === 'sun') return false;
  const arc = combustionArc(graha.graha, graha.speed < 0);
  return separation(graha.lon, sun.lon) <= arc;
}

/** Shortest angular separation between two longitudes, 0…180°. */
export function separation(a, b) {
  const d = Math.abs(norm360(a) - norm360(b));
  return d > 180 ? 360 - d : d;
}

/**
 * Heliocentric longitude of a planet, degrees, in the true ecliptic of date
 * (tropical, like everything in this layer).
 *
 * ◆ Exposed for ceṣṭā bala (Phase 6). The classical ceṣṭā-kendra is the arc
 * between the Sun's *mean* longitude and the planet's manda-corrected
 * ("true heliocentric") position; the second of those is exactly what this
 * returns, so the shaḍbala module does not have to rebuild an orbit.
 *
 * ⚠ Undefined for the Sun (it is the origin) and the Moon (its motion is about
 * the Earth); both throw.
 */
export function heliocentricLongitude(graha, jdTT) {
  const g = String(graha).toLowerCase();
  if (g === 'sun' || g === 'moon') {
    throw new RangeError(`heliocentricLongitude: "${graha}" has no heliocentric longitude`);
  }
  const body = bodyOf(g);
  return norm360(Ecliptic(HelioVector(body, timeFromTT(jdTT))).elon);
}

/**
 * The Sun's geometric mean longitude, degrees, referred to the mean equinox of
 * date — Meeus ch. 25, eq. 25.2. A uniformly advancing angle, which is what
 * makes it the "madhyama" reference of the classical ceṣṭā-kendra.
 */
export function sunMeanLongitude(jdTT) {
  const T = (jdTT - J2000) / 36525;
  return norm360(280.46646 + 36000.76983 * T + 0.0003032 * T * T);
}
